import Phaser from "phaser";
import { AGENTS, BUFFS, COLLECTIONS, CONTAINERS, ENEMIES, MAP_ASSET, PLAYER_FRAME_COUNT, PLAYER_FRAME_PREFIX, WEAPONS } from "../../shared/balance";
import { projectAsset } from "../core/assets";
import { GameBus } from "../core/EventBus";
import { createCroppedTexture, sourceBounds, unionBounds } from "./textures";
import { MAP_WORLD_HEIGHT, MAP_WORLD_WIDTH } from "../../shared/map";
import { getCollectionLootWidth } from "./objects";

export class BootScene extends Phaser.Scene {
  constructor() {
    super("Boot");
  }

  preload(): void {
    this.load.image("map", projectAsset(MAP_ASSET));
    for (let i = 1; i <= PLAYER_FRAME_COUNT; i++) {
      this.load.image(`player_${i}`, projectAsset(`${PLAYER_FRAME_PREFIX}${String(i).padStart(2, "0")}.png`));
    }
    for (const [key, config] of Object.entries(ENEMIES)) {
      this.load.image(`enemy_${key}`, projectAsset(config.asset));
    }
    for (const [key, config] of Object.entries(WEAPONS)) {
      this.load.image(`weapon_${key}`, projectAsset(config.asset));
    }
    for (const collection of COLLECTIONS) {
      this.load.image(`collection_${collection.id}`, projectAsset(collection.asset));
    }
    this.load.image("container_small", projectAsset(CONTAINERS.small.asset));
    this.load.image("container_large", projectAsset(CONTAINERS.large.asset));
    this.load.image("military_shell", projectAsset("assets/bullets/军用炮弹.png"));
    this.load.image("c4", projectAsset("assets/bullets/C4.png"));
    for (const agent of AGENTS) {
      for (let i = 1; i <= agent.frameCount; i++) {
        this.load.image(`agent_${agent.id}_${i}`, projectAsset(`${agent.animationPrefix}${String(i).padStart(2, "0")}.png`));
      }
    }
    for (let i = 1; i <= 9; i++) {
      this.load.image(`c4_boom_${i}`, projectAsset(`assets/effects/C4/frames/C4爆炸特效_spritesheet_1_frame_${String(i).padStart(2, "0")}.png`));
    }

    for (let i = 1; i <= 9; i++) {
      this.load.image(`boom_${i}`, projectAsset(`assets/effects/boom/frames/爆炸特效_spritesheet_1_frame_${String(i).padStart(2, "0")}.png`));
    }
    for (let i = 1; i <= 11; i++) {
      this.load.image(`fire_${i}`, projectAsset(`assets/effects/shotfire/frames/喷火特效_spritesheet_1_frame_${String(i).padStart(2, "0")}.png`));
    }
    for (let i = 1; i <= 8; i++) {
      this.load.image(`burn_${i}`, projectAsset(`assets/effects/burn/frames/燃烧特效_spritesheet_1_frame_${String(i).padStart(2, "0")}.png`));
    }
    for (let i = 1; i <= 9; i++) {
      this.load.image(`arrow_${i}`, projectAsset(`assets/effects/firearrow/frames/火箭_spritesheet_1_frame_${String(i).padStart(2, "0")}.png`));
    }
  }

  create(): void {
    const maximumViewport = Math.max(this.scale.width, this.scale.height, window.innerWidth, window.innerHeight, window.screen.width, window.screen.height);
    const maximumCameraZoom = Math.max(1, maximumViewport / Math.max(MAP_WORLD_WIDTH, MAP_WORLD_HEIGHT));
    const textureQualityScale = Math.max(6, Math.ceil(maximumCameraZoom * (window.devicePixelRatio || 1) * 2));
    const frameBounds = Array.from({ length: PLAYER_FRAME_COUNT }, (_, index) => sourceBounds(this, `player_${index + 1}`));
    const playerUnion = unionBounds(frameBounds);
    const enemyWidths: Record<string, number> = { soldier: 34, shield: 46, rocket: 58, gunner: 58, flamer: 52, boss: 64 };
    const weaponWidths: Record<string, number> = { g18: 54, uzi: 70, f12: 96, akm: 82, awm: 96 };
    for (let i = 1; i <= PLAYER_FRAME_COUNT; i++) {
      createCroppedTexture(this, `player_${i}`, `crop_player_${i}`, playerUnion, Math.round(64 * textureQualityScale));
    }
    for (const key of Object.keys(ENEMIES)) createCroppedTexture(this, `enemy_${key}`, `crop_enemy_${key}`, undefined, Math.round((enemyWidths[key] ?? 34) * textureQualityScale));
    for (const key of Object.keys(WEAPONS)) createCroppedTexture(this, `weapon_${key}`, `crop_weapon_${key}`, undefined, Math.round((weaponWidths[key] ?? 54) * textureQualityScale));
    for (const collection of COLLECTIONS) createCroppedTexture(this, `collection_${collection.id}`, `crop_collection_${collection.id}`, undefined, Math.round(getCollectionLootWidth(collection) * textureQualityScale));
    createCroppedTexture(this, "container_small", "crop_container_small", undefined, Math.round(44 * textureQualityScale));
    createCroppedTexture(this, "container_large", "crop_container_large", undefined, Math.round(50 * textureQualityScale));
    createCroppedTexture(this, "military_shell", "crop_military_shell", undefined, Math.round(52 * textureQualityScale));
    createCroppedTexture(this, "c4", "crop_c4", undefined, Math.round(36 * textureQualityScale));
    for (const agent of AGENTS) {
      const agentBounds = Array.from({ length: agent.frameCount }, (_, i) => sourceBounds(this, `agent_${agent.id}_${i + 1}`));
      const agentUnion = unionBounds(agentBounds);
      for (let i = 1; i <= agent.frameCount; i++) {
        createCroppedTexture(this, `agent_${agent.id}_${i}`, `crop_agent_${agent.id}_${i}`, agentUnion, Math.round(agent.defaultWidth * textureQualityScale));
      }
    }
    const c4BoomBounds = Array.from({ length: 9 }, (_, i) => sourceBounds(this, `c4_boom_${i + 1}`));
    const c4BoomUnion = unionBounds(c4BoomBounds);
    for (let i = 1; i <= 9; i++) createCroppedTexture(this, `c4_boom_${i}`, `crop_c4_boom_${i}`, c4BoomUnion, Math.round(160 * textureQualityScale));

    const boomBounds = Array.from({ length: 9 }, (_, i) => sourceBounds(this, `boom_${i + 1}`));
    const boomUnion = unionBounds(boomBounds);
    for (let i = 1; i <= 9; i++) createCroppedTexture(this, `boom_${i}`, `crop_boom_${i}`, boomUnion, Math.round(140 * textureQualityScale));
    const fireBounds = Array.from({ length: 11 }, (_, i) => sourceBounds(this, `fire_${i + 1}`));
    const fireUnion = unionBounds(fireBounds);
    for (let i = 1; i <= 11; i++) createCroppedTexture(this, `fire_${i}`, `crop_fire_${i}`, fireUnion, Math.round(250 * textureQualityScale));
    const burnBounds = Array.from({ length: 8 }, (_, i) => sourceBounds(this, `burn_${i + 1}`));
    const burnUnion = unionBounds(burnBounds);
    for (let i = 1; i <= 8; i++) createCroppedTexture(this, `burn_${i}`, `crop_burn_${i}`, burnUnion, Math.round(180 * textureQualityScale));
    const arrowBounds = Array.from({ length: 9 }, (_, i) => sourceBounds(this, `arrow_${i + 1}`));
    const arrowUnion = unionBounds(arrowBounds);
    for (let i = 1; i <= 9; i++) createCroppedTexture(this, `arrow_${i}`, `crop_arrow_${i}`, arrowUnion, Math.round(88 * textureQualityScale));
    this.createBulletTextures();
    this.createEffectAnimations();
    GameBus.emit("boot:ready", undefined);
  }

  private createEffectAnimations(): void {
    if (!this.anims.exists("boom_anim")) {
      this.anims.create({
        key: "boom_anim",
        frames: Array.from({ length: 9 }, (_, i) => ({ key: `crop_boom_${i + 1}` })),
        frameRate: 18,
        repeat: 0,
      });
    }
    if (!this.anims.exists("shotfire_anim")) {
      this.anims.create({
        key: "shotfire_anim",
        frames: Array.from({ length: 11 }, (_, i) => ({ key: `crop_fire_${i + 1}` })),
        frameRate: 18,
        repeat: -1,
      });
    }
    if (!this.anims.exists("burn_anim")) {
      this.anims.create({
        key: "burn_anim",
        frames: Array.from({ length: 8 }, (_, i) => ({ key: `crop_burn_${i + 1}` })),
        frameRate: 12,
        repeat: -1,
      });
    }
    if (!this.anims.exists("firearrow_anim")) {
      this.anims.create({
        key: "firearrow_anim",
        frames: Array.from({ length: 9 }, (_, i) => ({ key: `crop_arrow_${i + 1}` })),
        frameRate: 14,
        repeat: -1,
      });
    }

    for (const agent of AGENTS) {
      if (!this.anims.exists(`agent_${agent.id}_anim`)) {
        this.anims.create({
          key: `agent_${agent.id}_anim`,
          frames: Array.from({ length: agent.frameCount }, (_, i) => ({ key: `crop_agent_${agent.id}_${i + 1}` })),
          frameRate: agent.frameRate,
          repeat: -1,
        });
      }
    }
    if (!this.anims.exists("c4_boom_anim")) {
      this.anims.create({
        key: "c4_boom_anim",
        frames: Array.from({ length: 9 }, (_, i) => ({ key: `crop_c4_boom_${i + 1}` })),
        frameRate: 18,
        repeat: 0,
      });
    }
  }

  private createBulletTextures(): void {
    const specs = [
      { key: "bullet_g18", color: 0xffd54a, width: 14, height: 4 },
      { key: "bullet_uzi", color: 0x38e3c0, width: 10, height: 3 },
      { key: "bullet_f12", color: 0x62d76a, width: 16, height: 5 },
      { key: "bullet_akm", color: 0xff9c3f, width: 16, height: 5 },
      { key: "bullet_awm", color: 0xff3b3b, width: 24, height: 6 },
      { key: "enemy_bullet", color: 0xf4d23c, width: 10, height: 4 },
      { key: "shield_wave", color: 0xe76f51, width: 18, height: 10 },
      { key: "rocket", color: 0xff5522, width: 18, height: 8 },
      { key: "flame", color: 0xff9f43, width: 14, height: 6 },
    ];
    for (const { key, color, width, height } of specs) {
      const g = this.add.graphics();
      g.fillStyle(color, 1);
      g.fillRect(0, 0, width, height);
      g.fillStyle(0xffffff, 0.65);
      g.fillRect(2, Math.floor(height / 2) - 1, Math.max(2, Math.floor(width / 2)), 2);
      g.generateTexture(key, width, height);
      g.destroy();
    }
  }
}
